'use client';

import { useCallback, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import {
  APPLICATION_SCOPE_LIFECYCLE_RECONCILED_SSE_EVENT,
  ASSET_MERGE_UPDATED_SSE_EVENT,
  BATCH_URL_PARAMETRIZATION_ASSETS_UPDATED_SSE_EVENT,
  BATCH_URL_PARAMETRIZATION_COMPLETED_SSE_EVENT,
  BATCH_URL_PARAMETRIZATION_FAILED_SSE_EVENT,
  CRAWLER_SESSION_STATS_UPDATED_SSE_EVENT,
  CRAWLER_SESSION_STATUS_CHANGED_SSE_EVENT,
} from '@terra/shared-contracts-applications';
import {
  shouldInvalidateAssetsOnStatsUpdate,
  toasterApi,
  type DiscoveryAssetStatsCounters,
} from '@terra/ui-design-system';
import { useSSE } from '../app/providers/SseProvider';
import { useInvalidateAssetsSearch } from './api/useAssetsSearch';
import { useInvalidateAssetCounts } from './useAssetCounts';
import { useInvalidateApplicationDomainTree } from './api/useApplicationDomainTree';
import {
  assetMergeToastTitleKey,
  isAssetMergeReviseCommand,
} from '../utils/asset-merge/asset-merge-selection';

const TERMINAL_CRAWLER_STATUSES = ['COMPLETED', 'FAILED', 'ABORTED'] as const;
const ASSET_REFRESH_COALESCE_MS = 250;
const POST_EVENT_POLL_COUNT = 3;
const POST_EVENT_POLL_INTERVAL_MS = 5_000;

export function useInvalidateDiscoveryAssets() {
  const invalidateAssetsSearch = useInvalidateAssetsSearch();
  const invalidateAssetCounts = useInvalidateAssetCounts();
  const invalidateApplicationDomainTree = useInvalidateApplicationDomainTree();

  return useCallback(
    async ({ applicationId }: { applicationId: string }) => {
      await Promise.all([
        invalidateAssetsSearch({ applicationId }),
        invalidateAssetCounts({ applicationId }),
        invalidateApplicationDomainTree({ applicationId }),
      ]);
    },
    [
      invalidateApplicationDomainTree,
      invalidateAssetCounts,
      invalidateAssetsSearch,
    ],
  );
}

export function useAssetsDiscoveryRefresh({
  applicationId,
  enabled = true,
}: {
  applicationId: string;
  enabled?: boolean;
}) {
  const { subscribe } = useSSE();
  const invalidateDiscoveryAssets = useInvalidateDiscoveryAssets();
  const tMerge = useTranslations('proxy.assets.merge');

  useEffect(() => {
    if (!enabled || !applicationId) {
      return;
    }

    const lastStatsBySession = new Map<string, DiscoveryAssetStatsCounters>();
    let isActive = true;
    let refreshTimeout: ReturnType<typeof setTimeout> | null = null;
    let followUpTimeoutIds: ReturnType<typeof setTimeout>[] = [];
    let isRefreshInFlight = false;
    let isRefreshQueued = false;

    const runRefresh = () => {
      if (!isActive) {
        return;
      }

      if (isRefreshInFlight) {
        isRefreshQueued = true;

        return;
      }

      isRefreshInFlight = true;
      void invalidateDiscoveryAssets({ applicationId })
        .catch((error: unknown) => {
          console.warn(
            'Discovery assets refresh failed; keeping the currently loaded assets',
            {
              error: error instanceof Error ? error : new Error(String(error)),
              applicationId,
            },
          );
        })
        .finally(() => {
          isRefreshInFlight = false;
          if (!isActive) {
            isRefreshQueued = false;

            return;
          }

          if (isRefreshQueued) {
            isRefreshQueued = false;
            runRefresh();
          }
        });
    };

    const scheduleRefresh = () => {
      if (refreshTimeout) {
        return;
      }

      refreshTimeout = setTimeout(() => {
        refreshTimeout = null;
        runRefresh();
      }, ASSET_REFRESH_COALESCE_MS);
    };

    const clearFollowUpRefreshes = () => {
      followUpTimeoutIds.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      followUpTimeoutIds = [];
    };

    const runFollowUpRefresh = () => {
      if (!isActive) {
        return;
      }

      runRefresh();
    };

    const schedulePostEventPollBurst = () => {
      clearFollowUpRefreshes();
      scheduleRefresh();
      followUpTimeoutIds = Array.from(
        { length: POST_EVENT_POLL_COUNT },
        (_, pollOffset) =>
          setTimeout(
            runFollowUpRefresh,
            (pollOffset + 1) * POST_EVENT_POLL_INTERVAL_MS,
          ),
      );
    };

    const unsubscribeStatusChanged = subscribe(
      CRAWLER_SESSION_STATUS_CHANGED_SSE_EVENT,
      (event) => {
        if (event.targetId !== applicationId) {
          return;
        }

        if (
          TERMINAL_CRAWLER_STATUSES.includes(
            event.status as (typeof TERMINAL_CRAWLER_STATUSES)[number],
          )
        ) {
          scheduleRefresh();
        }
      },
    );

    const unsubscribeStatsUpdated = subscribe(
      CRAWLER_SESSION_STATS_UPDATED_SSE_EVENT,
      (event) => {
        if (event.targetId !== applicationId) {
          return;
        }

        const next: DiscoveryAssetStatsCounters = {
          endpointsNew: event.stats.endpointsNew,
          webpagesNew: event.stats.webpagesNew,
        };
        const prev = lastStatsBySession.get(event.sessionId);

        if (shouldInvalidateAssetsOnStatsUpdate({ prev, next })) {
          scheduleRefresh();
        }

        lastStatsBySession.set(event.sessionId, next);
      },
    );

    const unsubscribeParametrizationCompleted = subscribe(
      BATCH_URL_PARAMETRIZATION_COMPLETED_SSE_EVENT,
      (event) => {
        if (event.targetId !== applicationId) {
          return;
        }

        schedulePostEventPollBurst();
      },
    );

    const unsubscribeParametrizationFailed = subscribe(
      BATCH_URL_PARAMETRIZATION_FAILED_SSE_EVENT,
      (event) => {
        if (event.targetId !== applicationId) {
          return;
        }

        schedulePostEventPollBurst();
      },
    );

    const unsubscribeParametrizationAssetsUpdated = subscribe(
      BATCH_URL_PARAMETRIZATION_ASSETS_UPDATED_SSE_EVENT,
      (event) => {
        if (event.targetId !== applicationId) {
          return;
        }

        schedulePostEventPollBurst();
      },
    );

    const unsubscribeScopeLifecycleReconciled = subscribe(
      APPLICATION_SCOPE_LIFECYCLE_RECONCILED_SSE_EVENT,
      (event) => {
        if (event.applicationId !== applicationId) {
          return;
        }

        scheduleRefresh();
      },
    );

    const unsubscribeAssetMergeUpdated = subscribe(
      ASSET_MERGE_UPDATED_SSE_EVENT,
      (event) => {
        if (event.applicationId !== applicationId) {
          return;
        }

        if (
          event.status === 'MERGE_DONE' ||
          event.status === 'AWAITING_FINISH' ||
          event.status === 'REVERTED'
        ) {
          scheduleRefresh();
          if (event.status === 'MERGE_DONE' && event.mode === 'IMMEDIATE') {
            toasterApi.success({
              title: tMerge(
                assetMergeToastTitleKey({
                  outcome: 'done',
                  isRevise: isAssetMergeReviseCommand({
                    survivorKind: event.survivorKind,
                    loserCount: event.loserIds?.length ?? 0,
                  }),
                }),
              ),
            });
          }

          return;
        }

        if (
          event.status === 'FAILED' ||
          event.status === 'NEEDS_FORCE_FINISH'
        ) {
          toasterApi.error({
            title: tMerge(
              assetMergeToastTitleKey({
                outcome: 'failed',
                isRevise: isAssetMergeReviseCommand({
                  survivorKind: event.survivorKind,
                  loserCount: event.loserIds?.length ?? 0,
                }),
              }),
            ),
            description: event.message,
          });
        }
      },
    );

    return () => {
      isActive = false;
      unsubscribeStatusChanged();
      unsubscribeStatsUpdated();
      unsubscribeParametrizationCompleted();
      unsubscribeParametrizationFailed();
      unsubscribeParametrizationAssetsUpdated();
      unsubscribeScopeLifecycleReconciled();
      unsubscribeAssetMergeUpdated();
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
        refreshTimeout = null;
      }

      clearFollowUpRefreshes();
      isRefreshQueued = false;
      lastStatsBySession.clear();
    };
  }, [applicationId, enabled, invalidateDiscoveryAssets, subscribe, tMerge]);
}
